import {
  LevelType,
  SizingCalculatorAttributesType,
  SpaceType
} from 'data/types';
import {
  AccordionCategoryConfig,
  sizingDefaultsCategories
} from 'utils/sizing-item-configs';
import { getHasAttic } from 'utils/structure-basics';

export type StructureSizingPresence = {
  aboveGradeWall: boolean;
  belowGradeWall: boolean;
  ceilingBelowRoofJoists: boolean;
  ceilingOnExposedBeams: boolean;
  ceilingBelowAttic: boolean;
  floorOverUnconditionedSpace: boolean;
  floorOverExposedSpace: boolean;
  slabFloor: boolean;
  basementFloor: boolean;
  verticalGlass: boolean;
  solidDoor: boolean;
  skylight: boolean;
};

function getSpaces(structure: LevelType[]): SpaceType[] {
  return structure.flatMap((level) =>
    level.sections.flatMap((section) => section.spaces)
  );
}

function hasSizingItems(
  spaces: SpaceType[],
  key: keyof SizingCalculatorAttributesType
): boolean {
  return spaces.some(
    (space) => (space.sizingAttributes?.[key]?.length ?? 0) > 0
  );
}

function collectWalls(spaces: SpaceType[]) {
  return spaces.flatMap((space) => [
    ...(space.sizingAttributes?.aboveGradeWalls ?? []),
    ...(space.sizingAttributes?.belowGradeWalls ?? [])
  ]);
}

export function getStructureSizingPresence(
  structure: LevelType[]
): StructureSizingPresence {
  const spaces = getSpaces(structure);
  const roofCeilings = spaces.flatMap(
    (space) => space.sizingAttributes?.ceilingsBelowRoof ?? []
  );
  const walls = collectWalls(spaces);

  return {
    aboveGradeWall: hasSizingItems(spaces, 'aboveGradeWalls'),
    belowGradeWall: hasSizingItems(spaces, 'belowGradeWalls'),
    ceilingBelowRoofJoists: roofCeilings.some(
      (ceiling) => ceiling.ceilingType !== 'On Exposed Beams'
    ),
    ceilingOnExposedBeams: roofCeilings.some(
      (ceiling) => ceiling.ceilingType === 'On Exposed Beams'
    ),
    ceilingBelowAttic:
      getHasAttic(structure) || hasSizingItems(spaces, 'ceilingsBelowAttic'),
    floorOverUnconditionedSpace: hasSizingItems(
      spaces,
      'floorsOverUnconditionedSpace'
    ),
    floorOverExposedSpace: hasSizingItems(spaces, 'floorsOverExposedSpace'),
    slabFloor: hasSizingItems(spaces, 'slabFloors'),
    basementFloor: hasSizingItems(spaces, 'basementFloors'),
    verticalGlass: walls.length > 0,
    solidDoor: walls.length > 0,
    skylight: roofCeilings.length > 0
  };
}

export function getVisibleSizingDefaultsCategories(
  structure: LevelType[]
): AccordionCategoryConfig[] {
  const presence = getStructureSizingPresence(structure);

  return sizingDefaultsCategories
    .map((category) => ({
      ...category,
      items: category.items.filter(
        (item) => presence[item.defaultsKey as keyof StructureSizingPresence]
      )
    }))
    .filter((category) => category.items.length > 0);
}
